import { supabase, Mentor } from '../lib/supabase';
import { MentorAreaConfig, MentorStudent, normalizeSocials } from '../types/mentorship';
import { getMentorshipBySlug } from '../data/mockMentorshipData';

export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // remove caracteres especiais
    .replace(/\s+/g, '-') // espaços para hífens
    .replace(/-+/g, '-') // colapsa hífens duplos
    .replace(/^-+|-+$/g, ''); // remove hífens nas bordas
};

export const buildDefaultMentorAreaConfig = (data: Partial<Mentor>, cleanSlug: string): MentorAreaConfig => {
  const fallback = getMentorshipBySlug(cleanSlug);
  const mentorName = data.name || cleanSlug.replace(/-/g, ' ').toUpperCase();
  const mentorTitle = data.title || 'Mentor Oficial';
  const avatarUrl = data.avatar_url || '';
  const whatsappUrl = data.whatsapp
    ? data.whatsapp.startsWith('http')
      ? data.whatsapp
      : `https://wa.me/${data.whatsapp.replace(/\D/g, '')}`
    : '';

  return {
    ...fallback,
    id: data.id || `m-${cleanSlug}`,
    slug: cleanSlug,
    mentorUserId: data.user_id || null,
    accessDurationDays: data.access_duration_days || 365,
    mentorName,
    mentorTitle,
    mentorAvatarUrl: avatarUrl,
    courseTitle: data.title ? `Mentoria ${data.title}` : `Mentoria Oficial - ${mentorName}`,
    tagline: data.bio || 'SUA JORNADA COMEÇA AQUI',
    branding: {
      primaryColor: '#121316',
      accentColor: '#0FFF55',
      coverImageUrl: data.banner_url || data.banner_vertical_url || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=1200',
      bannerBadge: '1.00',
    },
    bioConfig: {
      isEnabled: true,
      slug: cleanSlug,
      bioTitle: mentorName,
      bioSubtitle: mentorTitle,
      bioAvatarUrl: avatarUrl,
      socials: {
        instagram: data.instagram_url || '',
        youtube: '',
        whatsapp: whatsappUrl,
        linkedin: data.linkedin_url || '',
        website: data.website_url || '',
      },
      links: [
        {
          id: 'link-1',
          title: `⚡ Mentoria VIP - ${mentorName}`,
          subtitle: 'Acesse o programa oficial de mentoria e acompanhamento',
          url: `/mentoria-${cleanSlug}`,
          icon: 'Zap',
          isFeatured: true,
          badgeText: '🔥 OFICIAL',
        },
        ...(whatsappUrl
          ? [
              {
                id: 'link-2',
                title: '💬 Falar no WhatsApp Oficial',
                subtitle: 'Tire suas dúvidas diretamente com nossa equipe',
                url: whatsappUrl,
                icon: 'MessageSquare',
              },
            ]
          : []),
        {
          id: 'link-3',
          title: '🌐 Plataforma MasterClub VIP',
          subtitle: 'Acesse nossa comunidade exclusiva de tecnologia e networking',
          url: '/',
          icon: 'Globe',
        },
      ],
      theme: {
        accentColor: '#0FFF55',
        backgroundColor: '#0d0e11',
      },
    },
  };
};

export const mentorshipService = {
  /**
   * Buscar configuração da mentoria pelo slug no Supabase
   */
  fetchMentorshipConfig: async (slug: string): Promise<MentorAreaConfig> => {
    const cleanSlug = slug.replace(/^\/?(mentoria-|bio-|links-|bio\/)?/, '').toLowerCase();

    try {
      console.log('🔍 Buscando mentoria no Supabase com slug:', cleanSlug);
      const { data, error } = await supabase
        .from('mentors')
        .select('*')
        .or(`slug.eq.${cleanSlug},name.ilike.%${cleanSlug.replace(/-/g, ' ')}%`)
        .maybeSingle();

      if (error) {
        console.warn('⚠️ Erro na busca do mentor no Supabase, usando fallback local:', error.message);
        return getMentorshipBySlug(cleanSlug);
      }

      if (data) {
        const areaConfig = data.area_config && Object.keys(data.area_config).length > 0
          ? data.area_config
          : null;

        const baseDefault = buildDefaultMentorAreaConfig(data, data.slug || cleanSlug);

        if (areaConfig) {
          console.log('✅ Configuração da mentoria carregada do Supabase para:', cleanSlug);
          return {
            ...baseDefault,
            ...areaConfig,
            id: data.id || baseDefault.id,
            slug: data.slug || cleanSlug,
            mentorUserId: data.user_id || areaConfig.mentorUserId || null,
            accessDurationDays: data.access_duration_days || areaConfig.accessDurationDays || 365,
            mentorName: data.name || areaConfig.mentorName || baseDefault.mentorName,
            mentorTitle: data.title || areaConfig.mentorTitle || baseDefault.mentorTitle,
            mentorAvatarUrl: data.avatar_url || areaConfig.mentorAvatarUrl || baseDefault.mentorAvatarUrl,
            bioConfig: (() => {
              // Mescla socials de forma retrocompatível: SocialEntry[] e strings legadas
              const rawSocials: Record<string, any> = {
                ...(baseDefault.bioConfig?.socials || {}),
                ...(areaConfig.bioConfig?.socials || {}),
              };
              // Coluna legada instagram_url só preenche se area_config já não tem instagram
              if (!rawSocials.instagram && data.instagram_url) rawSocials.instagram = data.instagram_url;
              if (!rawSocials.whatsapp && data.whatsapp) rawSocials.whatsapp = data.whatsapp;
              // Remove websites legados placeholder
              if (rawSocials.website === 'https://masterclub.vip' || rawSocials.website === 'https://masterclubbr.vip') {
                rawSocials.website = (data.website_url && !data.website_url.includes('masterclub')) ? data.website_url : '';
              }
              return {
                ...baseDefault.bioConfig,
                ...(areaConfig.bioConfig || {}),
                slug: data.slug || cleanSlug,
                bioTitle: areaConfig.bioConfig?.bioTitle || data.name || baseDefault.mentorName,
                bioSubtitle: areaConfig.bioConfig?.bioSubtitle || data.title || baseDefault.mentorTitle,
                bioAvatarUrl: areaConfig.bioConfig?.bioAvatarUrl || data.avatar_url || baseDefault.mentorAvatarUrl,
                socials: normalizeSocials(rawSocials),
              };
            })(),
          };
        }

        console.log('ℹ️ Mentoria sem area_config customizada no Supabase, construindo dinamicamente:', data.name);
        return baseDefault;
      }

      return getMentorshipBySlug(cleanSlug);
    } catch (err) {
      console.error('❌ Erro ao carregar mentoria do Supabase:', err);
      return getMentorshipBySlug(cleanSlug);
    }
  },

  /**
   * Persistir alterações da mentoria no Supabase
   */
  saveMentorshipConfigToSupabase: async (
    slug: string,
    newConfig: MentorAreaConfig
  ): Promise<{ success: boolean; message: string }> => {
    const cleanSlug = (newConfig.slug || slug).replace(/^\/?(mentoria-|bio-|links-|bio\/)?/, '').toLowerCase();

    try {
      console.log('💾 Salvando alterações da mentoria no Supabase para:', cleanSlug);
      const { data: existingMentor } = await supabase
        .from('mentors')
        .select('id, area_config')
        .or(`slug.eq.${cleanSlug},name.ilike.%${cleanSlug.replace(/-/g, ' ')}%`)
        .maybeSingle();

      if (existingMentor) {
        const updateData: any = {
          area_config: newConfig,
          slug: cleanSlug,
          name: newConfig.mentorName,
          title: newConfig.mentorTitle,
          avatar_url: newConfig.mentorAvatarUrl,
          website_url: newConfig.bioConfig?.socials?.website || null,
          access_duration_days: newConfig.accessDurationDays || 365,
          updated_at: new Date().toISOString(),
        };

        if (newConfig.mentorUserId !== undefined) {
          updateData.user_id = newConfig.mentorUserId || null;
        }

        let { error } = await supabase
          .from('mentors')
          .update(updateData)
          .eq('id', existingMentor.id);

        if (error && (error.code === 'PGRST204' || error.message?.includes('user_id'))) {
          console.warn('⚠️ Coluna user_id não encontrada na tabela mentors, atualizando via area_config...');
          delete updateData.user_id;
          const retry = await supabase
            .from('mentors')
            .update(updateData)
            .eq('id', existingMentor.id);
          error = retry.error;
        }

        if (error) {
          console.error('❌ Erro ao atualizar mentoria no Supabase:', error);
          return { success: false, message: error.message };
        }

        console.log('✅ Mentoria atualizada com sucesso no Supabase!');
        return { success: true, message: 'Mentoria e Bio salvas com sucesso no banco de dados!' };
      } else {
        // Criar registro na tabela mentors se não existir
        const insertPayload: any = {
          slug: cleanSlug,
          name: newConfig.mentorName,
          title: newConfig.mentorTitle,
          avatar_url: newConfig.mentorAvatarUrl,
          website_url: newConfig.bioConfig?.socials?.website || null,
          access_duration_days: newConfig.accessDurationDays || 365,
          user_id: newConfig.mentorUserId || null,
          area_config: newConfig,
          is_active: true,
        };

        let { error } = await supabase.from('mentors').insert([insertPayload]);

        if (error && (error.code === 'PGRST204' || error.message?.includes('user_id'))) {
          console.warn('⚠️ Coluna user_id não encontrada na tabela mentors, salvando via area_config...');
          delete insertPayload.user_id;
          const retry = await supabase.from('mentors').insert([insertPayload]);
          error = retry.error;
        }

        if (error) {
          console.error('❌ Erro ao criar mentoria no Supabase:', error);
          return { success: false, message: error.message };
        }

        console.log('✅ Nova mentoria criada com sucesso no Supabase!');
        return { success: true, message: 'Nova página de mentor criada e salva com sucesso!' };
      }
    } catch (err: any) {
      console.error('❌ Erro inesperado ao salvar mentoria no Supabase:', err);
      return { success: false, message: err?.message || 'Erro ao salvar no banco' };
    }
  },

  /**
   * Atualizar slug de um mentor existente
   */
  updateMentorSlug: async (mentorId: string, rawSlug: string): Promise<{ success: boolean; message: string; slug?: string }> => {
    const cleanSlug = slugify(rawSlug);
    if (!cleanSlug) {
      return { success: false, message: 'Slug inválida fornecida' };
    }

    try {
      // Verificar se a slug já está em uso por outro mentor
      const { data: conflict } = await supabase
        .from('mentors')
        .select('id, name')
        .eq('slug', cleanSlug)
        .neq('id', mentorId)
        .maybeSingle();

      if (conflict) {
        return { success: false, message: `A slug "${cleanSlug}" já está sendo usada pelo mentor ${conflict.name}.` };
      }

      // Buscar o mentor para atualizar também seu area_config se existir
      const { data: mentor } = await supabase
        .from('mentors')
        .select('id, name, title, avatar_url, area_config')
        .eq('id', mentorId)
        .single();

      let updatedAreaConfig = mentor?.area_config;
      if (updatedAreaConfig) {
        updatedAreaConfig = {
          ...updatedAreaConfig,
          slug: cleanSlug,
          bioConfig: {
            ...(updatedAreaConfig.bioConfig || {}),
            slug: cleanSlug,
          },
        };
      } else if (mentor) {
        updatedAreaConfig = buildDefaultMentorAreaConfig(mentor, cleanSlug);
      }

      const { error } = await supabase
        .from('mentors')
        .update({
          slug: cleanSlug,
          ...(updatedAreaConfig ? { area_config: updatedAreaConfig } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq('id', mentorId);

      if (error) {
        return { success: false, message: error.message };
      }

      return { success: true, message: `Slug atualizada para "${cleanSlug}" com sucesso!`, slug: cleanSlug };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Erro ao atualizar slug' };
    }
  },

  /**
   * Criar um novo mentor com slug e bio inicializada
   */
  createMentorWithBio: async (payload: {
    name: string;
    title: string;
    slug: string;
    avatar_url?: string;
    whatsapp?: string;
    instagram_url?: string;
    website_url?: string;
    bio?: string;
    userId?: string;
  }): Promise<{ success: boolean; message: string; mentor?: Mentor }> => {
    const cleanSlug = slugify(payload.slug || payload.name);

    if (!cleanSlug) {
      return { success: false, message: 'Nome ou Slug são obrigatórios' };
    }

    try {
      // Verificar unicidade da slug
      const { data: conflict } = await supabase
        .from('mentors')
        .select('id, name')
        .eq('slug', cleanSlug)
        .maybeSingle();

      if (conflict) {
        return { success: false, message: `A slug "${cleanSlug}" já está em uso por ${conflict.name}. Escolha outra slug.` };
      }

      const initialAreaConfig = buildDefaultMentorAreaConfig(
        {
          name: payload.name,
          title: payload.title,
          avatar_url: payload.avatar_url,
          whatsapp: payload.whatsapp,
          instagram_url: payload.instagram_url,
          website_url: payload.website_url,
          bio: payload.bio,
        },
        cleanSlug
      );

      const mentorRecord = {
        name: payload.name.trim(),
        title: payload.title.trim(),
        bio: payload.bio?.trim() || `Mentor oficial na MasterClub em ${payload.title}.`,
        specialties: [payload.title.trim()],
        experience_years: 1,
        avatar_url: payload.avatar_url?.trim() || '',
        contact_email: '',
        whatsapp: payload.whatsapp?.trim() || '',
        instagram_url: payload.instagram_url?.trim() || '',
        website_url: payload.website_url?.trim() || null,
        session_types: ['Mentoria Individual', 'Acompanhamento VIP'],
        languages: ['Português'],
        availability: 'Sob agendamento',
        created_by: payload.userId || '00000000-0000-0000-0000-000000000000',
        user_id: payload.userId || null,
        access_duration_days: 365,
        is_active: true,
        slug: cleanSlug,
        area_config: initialAreaConfig,
      };

      let { data, error } = await supabase
        .from('mentors')
        .insert([mentorRecord])
        .select()
        .single();

      if (error && (error.code === 'PGRST204' || error.message?.includes('user_id'))) {
        console.warn('⚠️ Coluna user_id não encontrada na tabela mentors, salvando via area_config...');
        const { user_id, ...mentorRecordWithoutUserId } = mentorRecord;
        const retry = await supabase
          .from('mentors')
          .insert([mentorRecordWithoutUserId])
          .select()
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        return { success: false, message: error.message };
      }

      return {
        success: true,
        message: 'Nova página e Link de Bio criados com sucesso!',
        mentor: data as Mentor,
      };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Erro ao criar mentor' };
    }
  },

  /**
   * Garantir que todos os mentores tenham uma slug gerada
   */
  ensureAllMentorsHaveSlugs: async (): Promise<{ updatedCount: number; message: string }> => {
    try {
      const { data: mentorsWithoutSlug, error } = await supabase
        .from('mentors')
        .select('id, name, slug')
        .or('slug.is.null,slug.eq.""');

      if (error || !mentorsWithoutSlug || mentorsWithoutSlug.length === 0) {
        return { updatedCount: 0, message: 'Todos os mentores já possuem slugs válidas.' };
      }

      let count = 0;
      for (const m of mentorsWithoutSlug) {
        const generatedSlug = slugify(m.name) || `mentor-${m.id.substring(0, 6)}`;
        const res = await mentorshipService.updateMentorSlug(m.id, generatedSlug);
        if (res.success) {
          count++;
        }
      }

      return { updatedCount: count, message: `${count} mentores receberam novas slugs com sucesso!` };
    } catch (err: any) {
      return { updatedCount: 0, message: err?.message || 'Erro ao verificar slugs' };
    }
  },

  /**
   * Buscar lista consolidada de alunos da mentoria (de user_products e area_config.students)
   */
  getMentorStudents: async (mentorId: string, areaConfigStudents?: MentorStudent[]): Promise<MentorStudent[]> => {
    try {
      const studentMap = new Map<string, MentorStudent>();

      // 1. Carregar alunos armazenados no areaConfig
      if (areaConfigStudents && Array.isArray(areaConfigStudents)) {
        areaConfigStudents.forEach((st) => {
          if (!st.email) return;
          const key = st.email.toLowerCase().trim();
          studentMap.set(key, {
            ...st,
            status: st.status || 'active',
          });
        });
      }

      // 2. Carregar acessos registrados na tabela user_products
      if (mentorId) {
        const { data: products, error } = await supabase
          .from('user_products')
          .select('*')
          .eq('mentor_id', mentorId)
          .order('created_at', { ascending: false });

        if (!error && products && products.length > 0) {
          products.forEach((prod: any) => {
            if (!prod.buyer_email) return;
            const key = prod.buyer_email.toLowerCase().trim();
            const existing = studentMap.get(key);

            const expiresAt = prod.expires_at;
            const isExpired = expiresAt ? new Date(expiresAt).getTime() < Date.now() : false;
            const computedStatus: 'active' | 'expired' | 'pending' =
              prod.status === 'cancelled' || prod.status === 'refunded'
                ? 'expired'
                : isExpired
                ? 'expired'
                : 'active';

            const studentName =
              prod.metadata?.student_name ||
              prod.metadata?.name ||
              existing?.name ||
              prod.buyer_email.split('@')[0];

            const accessDate = prod.starts_at
              ? new Date(prod.starts_at).toLocaleDateString('pt-BR')
              : (existing?.accessDateText || new Date().toLocaleDateString('pt-BR'));

            studentMap.set(key, {
              id: prod.id || existing?.id || `st-${Math.random().toString(36).substr(2, 9)}`,
              name: studentName,
              email: prod.buyer_email,
              accessDateText: accessDate,
              progressPercentage: existing?.progressPercentage || 0,
              expiresAt: prod.expires_at,
              durationDays: prod.metadata?.duration_days,
              status: computedStatus,
            });
          });
        }
      }

      return Array.from(studentMap.values());
    } catch (err) {
      console.error('Erro ao buscar alunos do mentor:', err);
      return areaConfigStudents || [];
    }
  },

  /**
   * Conceder acesso manual a um aluno para a mentoria
   */
  grantStudentAccess: async (
    mentor: { id: string; name: string; slug?: string; access_duration_days?: number },
    studentData: { name: string; email: string; durationDays?: number }
  ): Promise<{ success: boolean; message: string; student?: MentorStudent }> => {
    try {
      const email = studentData.email.toLowerCase().trim();
      const name = studentData.name.trim();
      const days = studentData.durationDays || mentor.access_duration_days || 365;

      const startsAt = new Date();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + days);

      const transactionId = `manual-${mentor.id || 'm'}-${email}-${Date.now()}`;

      // 1. Tentar inserir em user_products
      let insertedId = `st-${Date.now()}`;
      if (mentor.id) {
        const { data: userProduct, error: upError } = await supabase
          .from('user_products')
          .insert([
            {
              buyer_email: email,
              mentor_id: mentor.id,
              product_type: 'mentorship',
              title: `Mentoria VIP - ${mentor.name}`,
              status: 'active',
              payment_gateway: 'manual',
              transaction_id: transactionId,
              starts_at: startsAt.toISOString(),
              expires_at: expiresAt.toISOString(),
              metadata: {
                student_name: name,
                duration_days: days,
                granted_at: startsAt.toISOString(),
              },
            },
          ])
          .select()
          .maybeSingle();

        if (!upError && userProduct) {
          insertedId = userProduct.id;
        } else if (upError) {
          console.warn('Aviso ao registrar em user_products:', upError.message);
        }
      }

      // 2. Atualizar area_config do mentor para manter sincronizado e tolerante a offline/local
      const cleanSlug = mentor.slug || slugify(mentor.name);
      const config = await mentorshipService.fetchMentorshipConfig(cleanSlug);

      const newStudent: MentorStudent = {
        id: insertedId,
        name,
        email,
        accessDateText: startsAt.toLocaleDateString('pt-BR'),
        progressPercentage: 0,
        durationDays: days,
        expiresAt: expiresAt.toISOString(),
        status: 'active',
      };

      const currentStudents = config.students || [];
      const updatedStudents = [
        newStudent,
        ...currentStudents.filter((s) => s.email.toLowerCase().trim() !== email),
      ];

      await mentorshipService.saveMentorshipConfigToSupabase(cleanSlug, {
        ...config,
        students: updatedStudents,
      });

      return {
        success: true,
        message: `Acesso de ${days} dias liberado com sucesso para ${name}!`,
        student: newStudent,
      };
    } catch (err: any) {
      console.error('Erro ao conceder acesso:', err);
      return { success: false, message: err?.message || 'Erro ao conceder acesso' };
    }
  },

  /**
   * Revogar acesso de um aluno
   */
  revokeStudentAccess: async (
    mentorId: string,
    slug: string,
    studentEmail: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const email = studentEmail.toLowerCase().trim();

      // 1. Atualizar user_products se existir mentorId
      if (mentorId) {
        await supabase
          .from('user_products')
          .update({ status: 'cancelled', updated_at: new Date().toISOString() })
          .eq('mentor_id', mentorId)
          .eq('buyer_email', email);
      }

      // 2. Atualizar area_config
      const cleanSlug = slugify(slug);
      const config = await mentorshipService.fetchMentorshipConfig(cleanSlug);
      const updatedStudents = (config.students || []).map((s) =>
        s.email.toLowerCase().trim() === email ? { ...s, status: 'expired' as const } : s
      );

      await mentorshipService.saveMentorshipConfigToSupabase(cleanSlug, {
        ...config,
        students: updatedStudents,
      });

      return { success: true, message: 'Acesso do aluno revogado com sucesso.' };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Erro ao revogar acesso' };
    }
  },

  /**
   * Estender acesso de um aluno por X dias
   */
  extendStudentAccess: async (
    mentorId: string,
    slug: string,
    studentEmail: string,
    additionalDays: number
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const email = studentEmail.toLowerCase().trim();
      const cleanSlug = slugify(slug);
      const config = await mentorshipService.fetchMentorshipConfig(cleanSlug);

      const targetStudent = (config.students || []).find(
        (s) => s.email.toLowerCase().trim() === email
      );

      const currentExpiry = targetStudent?.expiresAt ? new Date(targetStudent.expiresAt) : new Date();
      const baseDate = currentExpiry.getTime() > Date.now() ? currentExpiry : new Date();
      baseDate.setDate(baseDate.getDate() + additionalDays);
      const newExpiresAt = baseDate.toISOString();

      if (mentorId) {
        await supabase
          .from('user_products')
          .update({
            expires_at: newExpiresAt,
            status: 'active',
            updated_at: new Date().toISOString(),
          })
          .eq('mentor_id', mentorId)
          .eq('buyer_email', email);
      }

      const updatedStudents = (config.students || []).map((s) =>
        s.email.toLowerCase().trim() === email
          ? {
              ...s,
              expiresAt: newExpiresAt,
              status: 'active' as const,
              durationDays: (s.durationDays || 365) + additionalDays,
            }
          : s
      );

      await mentorshipService.saveMentorshipConfigToSupabase(cleanSlug, {
        ...config,
        students: updatedStudents,
      });

      return { success: true, message: `Acesso estendido por mais ${additionalDays} dias!` };
    } catch (err: any) {
      return { success: false, message: err?.message || 'Erro ao estender acesso' };
    }
  },
};
